import React, { useState, useEffect } from "react";
import { parseProductsFile } from "./ProductsParser";
import {
  updateProductInFile,
  deleteProductFromFile,
  exportToCSV,
  exportToXLSX,
} from "./productFIleManager";
import { fetchCategories } from "../../utils/dataFetcher";
import { useTranslation } from "react-i18next";
import EditProductModal from "./EditProductModel";

const PreviewBulk = ({
  onNext,
  onCancel,
  onBack,
  file,
  createBulkProducts,
  setIsUpdatedFile,
  errors,
}) => {
  const [products, setProducts] = useState([]);
  const [parsedData, setParsedData] = useState(null); // Store original parsed data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [categories, setCategories] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductErrors, setShowProductErrors] = useState(false);
  const [failedProduct, setFailedProduct] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletedProduct, setDeletedProduct] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [successNumber, setSuccessNumber] = useState(0);
  const [failedNumber, setFailedNumber] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "success", "failed"
  const failedProductRef = React.useRef(null);
  
  const itemsPerPage = 10;
  const { t } = useTranslation("bulkproducts");

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  useEffect(() => {
    setLoading(true);
    fetchCategories(getLangFromLocalStorage())
      .then(async (data) => {
        const categoriesData = data.data || data;
        setCategories(categoriesData);

        if (!file) {
          setError(t("error.noFile"));
          setLoading(false);
          return;
        }

        try {
          const result = await parseProductsFile(file, t);
          setParsedData(result); // Store original parsed data

          // Transform parsed data to match our component structure
          const transformedProducts = result.rows.map((row, index) => ({
            id: index + 1,
            photo: row.images
              ? row.images
              .split(/,(?=https?:\/\/)/) // split only when a new URL starts
                  .map((url) => url.trim())
                  .filter(Boolean)[0] // Take first image
              : "/api/placeholder/40/40",
            productName: row.name || row.name_ar || "Unnamed Product",
            type: row.category_id
              ? categoriesData.find((c) => c.id === row.category_id)?.name
              : "General",
            price: row.price || "0",
            sku: row.sku || "",
            description: row.description || row.description_ar || "",
            stock: row.stock || 0,
            min_order: row.min_order || 1,
            rawData: row, // Keep original data for editing
          }));
          console.log("Transformed products:", transformedProducts);
          setProducts(transformedProducts);
          setError(null);
        } catch (err) {
          console.error("Error parsing file:", err);
          setError(err.message || t("error.title"));
        } finally {
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Error fetching categories:", err);
        setError(t("error.fetchCategories"));
        setLoading(false);
      });
  }, [file]);

  const handleEdit = (productId) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setEditingProduct(product);
      setEditModalOpen(true);
    }
    setDropdownOpen(null);
  };

  const handleSaveEdit = async (productId, updates) => {
    try {
      const product = products.find((p) => p.id === productId);
      if (!product) {
        throw new Error("Product not found");
      }

      // Use SKU or name as identifier for the file manager
      const identifier = product.sku || product.productName;

      // Update the parsed data using file manager
      const updatedParsedData = updateProductInFile(
        parsedData,
        identifier,
        updates
      );
      setParsedData(updatedParsedData);
      setIsUpdatedFile(true); // Mark that the file has been updated
      // Update the local products state
      setProducts((prevProducts) => {
        return prevProducts.map((p) => {
          if (p.id === productId) {
            return {
              ...p,
              productName: updates.name || p.productName,
              price: updates.price || p.price,
              sku: updates.sku || p.sku,
              description: updates.description || p.description,
              stock: updates.stock || p.stock,
              min_order: updates.min_order || p.min_order,
              photo: updates.images
                ? updates.images.split(/,(?=https?:\/\/)/)[0].trim()
                : p.photo,
              type: updates.category_id
                ? categories.find((c) => c.id === parseInt(updates.category_id))
                    ?.name || p.type
                : p.type,
               shipping_period: updates.shipping_period || p.shipping_period,
              rawData: {
                ...p.rawData,
                ...updates,
              },
            };
          }
          return p;
        });
      });
     // update fixedProducts state
      console.log("Product updated successfully", updatedParsedData);
    } catch (error) {
      console.error("Error updating product:", error);
      throw error; // Re-throw to be handled by the modal
    }
  };

  const handleDelete = (product, forceDelete = false) => {
    if (!forceDelete) {
      setShowDeleteConfirm(true);
      setDeletedProduct(product);
      return;
    }
    setDeleting(true);
    try {
      if (!product) {
        throw new Error("Product not found");
      }

      // Use SKU or name as identifier for the file manager
      const identifier = product.sku || product.productName;

      // Update the parsed data using file manager
      const updatedParsedData = deleteProductFromFile(parsedData, identifier);
      setParsedData(updatedParsedData);
      // Update the local products using the updated parsed data
      setProducts((prevProducts) =>
        prevProducts.filter((p) => p.sku !== product.sku)
      );
      console.log("Updated parsed data after deletion:", updatedParsedData);
      // update failed and success numbers based on this product deletion
      if (errors && errors.length > 0) {
        const isFailed = errors.find((e) => e.sku === product.sku);
        if (isFailed) {
          setFailedNumber((prev) => prev - 1);
        } else {
          setSuccessNumber((prev) => prev - 1);
        }
      }

     
      setIsUpdatedFile(true); // Mark that the file has been updated
      console.log("Product deleted successfully");
    } catch (error) {
      console.error("Error deleting product:", error);
      throw error;
    }finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setDropdownOpen(null);
      
    }
  };

  const handleModalDelete = async (product) => {
    console.log("Deleting product:", product);
    await handleDelete(product, true);
  };

  const toggleDropdown = (productId) => {
    setDropdownOpen(dropdownOpen === productId ? null : productId);
  };

  const handlePublish = async () => {
    if (products.length === 0) return;
    setPublishing(true);
    try {
      // Option 1: Send the updated file to backend

      console.log("Updated parsedData:", parsedData);
      const updatedFile = exportToCSV(parsedData, `updated-${file.name}`);

      // You can either:
      // A) Send the file to backend first, then call createBulkProducts
      // B) Or pass the updated parsed data directly to createBulkProducts

      const result = await createBulkProducts(updatedFile); // Pass both for flexibility
      console.log("Bulk products created:", result);
      onNext(result);
    } catch (error) {
      console.error("Error creating bulk products:", error);
    } finally {
      setPublishing(false);
    }
  };

  // Export current data
  const handleExportCSV = () => {
    try {
      const exportedFile = exportToCSV(
        parsedData,
        `updated-${file.name.replace(/\.[^/.]+$/, "")}.csv`
      );
      const url = URL.createObjectURL(exportedFile);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportedFile.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV:", error);
    }
  };

  const handleExportXLSX = () => {
    try {
      const exportedFile = exportToXLSX(
        parsedData,
        `updated-${file.name.replace(/\.[^/.]+$/, "")}.xlsx`
      );
      const url = URL.createObjectURL(exportedFile);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportedFile.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting XLSX:", error);
    }
  };

  // add status column to show if the product will be created or failed based on errors prop
  const getStatus = (product) => {
    const error = errors.find((e) => e.row === product.id +1);
    return error ? true : false;
  };

  // Filter products based on status
  const filteredProducts = products.filter((product) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "success") return !getStatus(product);
    if (statusFilter === "failed") return getStatus(product);
    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setDropdownOpen(null); // Close any open dropdowns when changing pages
  };

  const handleStatusFilter = (filter) => {
    setStatusFilter(filter);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };
  // handle show product errors
  const handleShowErrors = (Product) => {
    setShowProductErrors(true);
    setFailedProduct(Product);
    
  };
 // set successNumber and failedNumber based on errors prop
  useEffect(() => {
    if (errors && errors.length > 0) {
      setFailedNumber(errors.length);
      setSuccessNumber(products.length - errors.length);
    } else {
      setFailedNumber(0);
      setSuccessNumber(products.length);
    }
  }, [errors]);
// render products if changed
  useEffect(() => {
    console.log("Products updated:", products);
  }, [products]);
  //close the showProductErrors when click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProductErrors && failedProductRef.current && !failedProductRef.current.contains(event.target)) {
        setShowProductErrors(false);
        setFailedProduct(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProductErrors]);


  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-2 text-gray-600">{t("parsing")}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <h3 className="text-red-800 font-medium">{t("error.title")}</h3>
          <p className="text-red-600 text-sm mt-1">{error}</p>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t("preview.cancel")}
          </button>
        </div>
      </div>
    );
  }
 

  return (
    <div className="mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t("preview.title")}
            </h2>
            {/* Products Successfully (1400)  Products Failed (400)  */}
            {errors && errors.length > 0 ? (
              <div className="mt-[24px] mb-[12px]">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => handleStatusFilter("success")}
                    className={`text-[16px] font-[600] transition-colors ${
                      statusFilter === "success"
                        ? "text-[#5507CF] border-b-[3px] border-[#5507CF] py-[4px]"
                        : "text-[#198754] hover:text-[#5507CF]"
                    }`}
                  >
                    {t("preview.productsSuccess")} ({successNumber})
                  </button>
                  <button
                    onClick={() => handleStatusFilter("failed")}
                    className={`text-[16px] font-[600] transition-colors ${
                      statusFilter === "failed"
                        ? "text-[#5507CF] border-b-[3px] border-[#5507CF] py-[4px]"
                        : "text-[#DC3545] hover:text-[#5507CF]"
                    }`}
                  >
                    {t("preview.productsFailed")} ({failedNumber})
                  </button>
                  <button
                    onClick={() => handleStatusFilter("all")}
                    className={`text-[16px] font-[600] transition-colors ${
                      statusFilter === "all"
                        ? "text-[#5507CF] border-b-[3px] border-[#5507CF] py-[4px]"
                        : "text-gray-600 hover:text-[#5507CF]"
                    }`}
                  >
                    All ({products.length})
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600 mt-1">
                {t("preview.summary", {
                  count: filteredProducts.length,
                  from: startIndex + 1,
                  to: Math.min(endIndex, filteredProducts.length),
                  total: filteredProducts.length,
                })}
              </p>
            )}
          </div>

          {/* Export buttons */}
          {/* <div className="flex gap-2">
            <button
              onClick={handleExportCSV}
              className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
            <button
              onClick={handleExportXLSX}
              className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Excel
            </button>
          </div> */}
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white border rounded-lg overflow-hidden mb-6">
        <table className="w-full">
          <thead className="bg-[#EBE5FF] border-b-2 border-[#5507CF]">
            <tr>
              <th className="px-4 py-3 text-start text-[#5507CF] text-[14px] font-[400]">
                {t("preview.sku")}
              </th>
              <th className="px-4 py-3 text-start text-[#5507CF] text-[14px] font-[400]">
                {t("preview.productName")}
              </th>
              <th className="px-4 py-3 text-start text-[#5507CF] text-[14px] font-[400]">
                {t("preview.type")}
              </th>
              <th className="px-4 py-3 text-start text-[#5507CF] text-[14px] font-[400]">
                {t("preview.price")}
              </th>
              {/* number of stock */}
              <th className="px-4 py-3 text-start text-[#5507CF] text-[14px] font-[400]">
                {t("preview.stock")}
              </th>
              {/* status based on submition */}
              {errors && errors.length > 0 && (
                <th className="px-4 py-3 text-start text-[#5507CF] text-[14px] font-[400]">
                  {t("preview.status")}
                </th>
              )}
              
              {/* <th className="px-4 py-3 text-start text-[#5507CF] text-[14px] font-[400]">
                {t("preview.action")}
              </th> */}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {currentProducts.map((product, index) => (
              <tr
                key={product.id}
                // className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
              >
               <td className="px-4 py-4">
                  <div className="text-[16px] font-[600]">
                    {product.sku || "N/A"}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-[16px] font-[600]">
                    {product.productName}
                  </div>
                </td>
                <td className="px-4 py-4 text-[16px] font-[600]">
                  {product.type}
                </td>
                <td className="px-4 py-4">
                  <div className="text-[16px] font-[600]">{product.price}</div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-[16px] font-[600]">{product.stock}</div>
                </td>
                {/* status based on  failed or successful submission */}
                {errors && errors.length > 0 && (
                  <td className="px-4 py-4">
                    {/* #DC3545 for failed and #198754 for success */}
                    <div
                      className={`text-[16px] font-[600] flex items-center gap-[8px] ${
                        getStatus(product)
                          ? "text-[#DC3545] cursor-pointer"
                          : "text-[#198754]"
                      }`}
                      {...(getStatus(product)
                        ? { onClick: () => handleShowErrors(product) }
                        : {})}
                    >
                      {getStatus(product)
                        ? t("preview.failed")
                        : t("preview.success")}
                      {/* if failed show svg*/}
                      {getStatus(product) && (
                        <svg
                          width="25"
                          height="24"
                          viewBox="0 0 25 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18.5 10.5V19.125C18.5 19.3712 18.4515 19.615 18.3573 19.8425C18.263 20.07 18.1249 20.2767 17.9508 20.4508C17.7767 20.6249 17.57 20.763 17.3425 20.8573C17.115 20.9515 16.8712 21 16.625 21H5.375C4.87772 21 4.40081 20.8025 4.04917 20.4508C3.69754 20.0992 3.5 19.6223 3.5 19.125V7.875C3.5 7.37772 3.69754 6.90081 4.04917 6.54917C4.40081 6.19754 4.87772 6 5.375 6H13.2256M16.25 3H21.5V8.25M11 13.5L21.125 3.375"
                            stroke="#DC3545"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </td>
                )}

                {/* <td className="px-4 py-4 relative">
                  <button
                    onClick={() => toggleDropdown(product.id)}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 22.75C6.07 22.75 1.25 17.93 1.25 12C1.25 6.07 6.07 1.25 12 1.25C17.93 1.25 22.75 6.07 22.75 12C22.75 17.93 17.93 22.75 12 22.75ZM12 2.75C6.9 2.75 2.75 6.9 2.75 12C2.75 17.1 6.9 21.25 12 21.25C17.1 21.25 21.25 17.1 21.25 12C21.25 6.9 17.1 2.75 12 2.75Z"
                        fill="#5507CF"
                      />
                      <path
                        d="M12 13C11.44 13 11 12.55 11 12C11 11.45 11.45 11 12 11C12.55 11 13 11.45 13 12C13 12.55 12.56 13 12 13Z"
                        fill="#5507CF"
                      />
                      <path
                        d="M16 13C15.44 13 15 12.55 15 12C15 11.45 15.45 11 16 11C16.55 11 17 11.45 17 12C17 12.55 16.56 13 16 13Z"
                        fill="#5507CF"
                      />
                      <path
                        d="M8 13C7.44 13 7 12.55 7 12C7 11.45 7.45 11 8 11C8.55 11 9 11.45 9 12C9 12.55 8.56 13 8 13Z"
                        fill="#5507CF"
                      />
                    </svg>
                  </button>

                  {dropdownOpen === product.id && (
                    <div className="absolute right-[32px] rtl:right-0 rtl:left-[32px] top-[-8px] mt-2 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                      <button
                        onClick={() => handleEdit(product.id)}
                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                        {t("preview.edit")}
                      </button>
                      <button
                        onClick={() => handleDelete(product)}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        {t("preview.delete")}
                      </button>
                    </div>
                  )}
                </td> */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mb-6">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-2 ${
              currentPage === 1
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-100"
            } rounded`}
          >
            <img src="/arrow--left.svg" alt="Previous" className="w-5 h-5" />
          </button>

          <div className="flex gap-1 mx-4">
            {getPageNumbers().map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`px-3 py-2 rounded ${
                  currentPage === pageNum
                    ? "bg-[#6609F8] text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {pageNum}
              </button>
            ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`p-2 ${
              currentPage === totalPages
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-100"
            } rounded`}
          >
            <img src="/arrow--right.svg" alt="Next" className="w-5 h-5" />
          </button>
        </div>
      )}

      {filteredProducts.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          {statusFilter === "all" 
            ? t("preview.noProducts")
            : statusFilter === "success"
            ? t("preview.noSuccessProducts")
            : t("preview.noFailedProducts")
          }
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="w-[163px] h-[43px] leading-[1] px-[8px] py-[12px] border border-[#292929] text-[#292929] font-[400] text-[16px] rounded-[8px] hover:bg-gray-50 transition-colors"
        >
          {t("preview.cancel")}
        </button>
        {/* IN CASE of errors  show back button */}
        {(errors && errors.length > 0) ? (
          <button
            onClick={onBack}
            className="bg-[#6609F8] w-[163px] h-[43px] leading-[1] px-[8px] py-[12px] border border-[#6609F8] text-white font-[400] text-[16px] rounded-[8px] hover:bg-[#6609F8] transition-colors"
          >
            {t("preview.back")}
          </button>
        ):
        <button
          onClick={handlePublish}
          disabled={products.length === 0 || publishing}
          className={`w-[260px] h-[43px] leading-[1] px-[8px] py-[12px] rounded-[8px] transition-colors ${
            products.length > 0 && !publishing
              ? "bg-[#6609F8] text-white hover:bg-[#6609F8]"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {publishing ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {t("preview.publishing")}
            </div>
          ) : (
            t("preview.publish")
          )}
        </button>
}
      </div>

      {/* Edit Product Modal */}
      <EditProductModal
        isOpen={editModalOpen}
        // showEditDeleteConfirm={showEditDeleteConfirm}
        // setShowEditDeleteConfirm={setShowEditDeleteConfirm}
        onClose={() => {
          setEditModalOpen(false);
          setEditingProduct(null);
          // setShowEditDeleteConfirm(false);
        }}
        product={editingProduct}
        categories={categories}
        onSave={handleSaveEdit}
        onDelete={handleModalDelete}
      />

      {/* Click outside to close dropdown */}
      {dropdownOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setDropdownOpen(null)}
        />
      )}
      {/* Product Errors */}

      {showProductErrors && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 border border-[#DC3545]" ref={failedProductRef}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {t("preview.productUploadError")}
              </h2>
              <button
                onClick={() => {
                  setFailedProduct(null);
                  setShowProductErrors(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Product Name Section */}
              <div className="mb-6">
                <h3 className="text-[16px] font-[600]  mb-3">
                  {t("preview.productName")}
                </h3>
                {/* Find the first failed product to show as example */}
                <div className="text-[16px] font-[500] ">
                  {failedProduct ? failedProduct.productName : "N/A"}
                </div>
              </div>

              {/* Reasons Section */}
              <div className="mb-6">
                <h3 className="text-[16px] font-[600]  mb-3">
                  {t("preview.reasons")}:
                </h3>
                <div className="py-[12px] px-[8px] border border-[#EDEDED] rounded-[8px] ">
                <ul className="space-y-2">
                  {/* Show errors for failed product */}
                  {failedProduct &&
                    errors
                      .filter((error) => error.row === failedProduct.id + 1)
                      .map((error, index) => (
                        <li
                          key={`${failedProduct.id}-${index}`}
                          className="flex items-start gap-2"
                        >
                          <span className="w-2 h-2 bg-[#000000] rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-[16px] font-[500] ">
                            {error.errors.join(", ")}
                          </span>
                        </li>
                      ))}
                </ul>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            {/* <div className="flex flex-grow gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowProductErrors(false)}
                className="w-full px-6 py-2 border border-[#5507CF] text-[#5507CF] rounded-lg hover:bg-purple-50 transition-colors font-medium"
              >
                {t("preview.cancel")}
              </button>
              <button
                onClick={() => {
                
                    setShowProductErrors(false);
                    handleEdit(failedProduct.id);
                  
                }}
                className="w-full px-6 py-2 bg-[#DC3545] text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
              >
                {t("preview.edit")}
              </button>
            </div> */}
          </div>
        </div>
      )}
       {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("edit.confirmDelete")}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {t("edit.deleteWarning")}
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm font-medium text-gray-700">
                  {deletedProduct?.productName}
                </p>
                {deletedProduct?.sku && (
                  <p className="text-xs text-gray-500">SKU: {deletedProduct.sku}</p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t("edit.cancelDelete")}
                </button>
                <button
                  onClick={() => handleDelete(deletedProduct, true)}
                  disabled={deleting}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                    deleting
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700"
                  }`}
                >
                  {deleting && (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {deleting ? t("edit.deleting") : t("edit.confirmDeleteBtn")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreviewBulk;
